import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import FidesChatAdminService, { ChatMessage } from '../../services/admin/FidesChatAdminService';
import {
  getFidesChatFirestore,
  CONVERSATIONS_COLLECTION,
} from '../../services/admin/FidesChatFirebase';
import type { ChatParticipantAddedEvent } from '../../utils/chatParticipantAddedNote';

function normalizeMessage(data: Partial<ChatMessage>, fallbackId: string): ChatMessage {
  return {
    id: data.id ?? fallbackId,
    senderId: data.senderId ?? '',
    senderName: data.senderName ?? '',
    senderRole: data.senderRole === 'admin' ? 'admin' : 'investor',
    text: data.text ?? '',
    timestamp: data.timestamp ?? null,
    context: data.context ?? null,
  };
}

function normalizeEvent(
  data: Partial<ChatParticipantAddedEvent>,
  fallbackId: string,
): ChatParticipantAddedEvent | null {
  if (data.type !== 'participant_added') return null;
  return {
    id: data.id ?? fallbackId,
    type: 'participant_added',
    inviterAdminExternalId: data.inviterAdminExternalId ?? '',
    inviterName: data.inviterName ?? '',
    inviteeAdminExternalId: data.inviteeAdminExternalId ?? '',
    inviteeName: data.inviteeName ?? '',
    timestamp: data.timestamp ?? null,
  };
}

export default function useFidesChatMessages(conversationId: string | null, enabled = true) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [events, setEvents] = useState<ChatParticipantAddedEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !conversationId) {
      setMessages([]);
      setEvents([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let isMounted = true;
    let unsubscribeMessages: (() => void) | undefined;
    let unsubscribeEvents: (() => void) | undefined;

    setLoading(true);
    setError(null);

    void FidesChatAdminService.getConversationTimeline(conversationId)
      .then((timeline) => {
        if (!isMounted) return;
        setMessages(timeline.messages);
        setEvents(timeline.events);
        setLoading(false);
      })
      .catch((historyError) => {
        console.error('Fides chat history load failed', historyError);
        if (isMounted) {
          setError('Unable to load chat history.');
          setLoading(false);
        }
      });

    void (async () => {
      try {
        const firestore = await getFidesChatFirestore();

        if (!isMounted) {
          return;
        }

        unsubscribeMessages = onSnapshot(
          query(
            collection(firestore, CONVERSATIONS_COLLECTION, conversationId, 'messages'),
            orderBy('timestamp', 'asc'),
          ),
          (snapshot) => {
            if (!isMounted) return;
            setMessages(
              snapshot.docs
                .filter((messageDoc) => {
                  const senderRole = (messageDoc.data() as { senderRole?: string }).senderRole;
                  return senderRole !== 'system';
                })
                .map((messageDoc) => {
                  const data = messageDoc.data() as Partial<ChatMessage>;
                  return normalizeMessage(data, messageDoc.id);
                }),
            );
            setError(null);
            setLoading(false);
          },
          (snapshotError) => {
            console.error('Fides chat message snapshot failed', snapshotError);
            if (isMounted) {
              setError('Live chat updates are temporarily unavailable.');
              setLoading(false);
            }
          },
        );

        unsubscribeEvents = onSnapshot(
          query(
            collection(firestore, CONVERSATIONS_COLLECTION, conversationId, 'events'),
            orderBy('timestamp', 'asc'),
          ),
          (snapshot) => {
            if (!isMounted) return;
            setEvents(
              snapshot.docs
                .map((eventDoc) =>
                  normalizeEvent(
                    eventDoc.data() as Partial<ChatParticipantAddedEvent>,
                    eventDoc.id,
                  ),
                )
                .filter((event): event is ChatParticipantAddedEvent => event !== null),
            );
            setLoading(false);
          },
          (snapshotError) => {
            console.error('Fides chat event snapshot failed', snapshotError);
          },
        );
      } catch (firestoreError) {
        console.error('Fides chat Firebase setup failed', firestoreError);
        if (isMounted) {
          setError((current) => current ?? 'Live chat updates are temporarily unavailable.');
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
      unsubscribeMessages?.();
      unsubscribeEvents?.();
    };
  }, [conversationId, enabled]);

  return { messages, events, loading, error };
}
